import { useState } from 'react';
import { api, ApiError } from '../../api';
import type { RecordPaymentPayload } from '../../api';
import { useToast } from '../../components/Toast';
import {
  Sheet,
  Field,
  FieldRow,
  TextInput,
  MoneyInput,
  SelectInput,
  ErrorBanner,
  PrimaryBtn,
  GhostBtn,
  rupeesToPaise,
  dateInputToEpoch,
  epochToDateInput,
  currentPeriodUTC,
} from './Sheet';

const METHOD_OPTS = [
  { value: 'neft', label: 'NEFT / bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

/**
 * Record a rent payment against a tenancy. Links to a "YYYY-MM" ledger period
 * (created if needed) so that month flips to Paid. Money ₹ → paise.
 */
export function RecordPayment({
  tenancyId,
  tenantName,
  defaultPeriod,
  onClose,
  onRecorded,
}: {
  tenancyId: string;
  tenantName?: string;
  defaultPeriod?: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const notify = useToast();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<NonNullable<RecordPaymentPayload['method']>>('neft');
  const [reference, setReference] = useState('');
  const [period, setPeriod] = useState(defaultPeriod || currentPeriodUTC());
  const [paidOn, setPaidOn] = useState(epochToDateInput(Date.now()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountPaise = rupeesToPaise(amount);
  const canSubmit = amountPaise != null && amountPaise > 0 && /^\d{4}-\d{2}$/.test(period);

  async function submit() {
    setError(null);
    if (amountPaise == null || amountPaise <= 0) return setError('Enter a valid payment amount.');
    if (!/^\d{4}-\d{2}$/.test(period)) return setError('Pick the month this payment is for.');

    setBusy(true);
    try {
      const payload: RecordPaymentPayload = {
        amount: amountPaise,
        method,
        reference: reference.trim() || undefined,
        ledger_period: period,
        paid_on: dateInputToEpoch(paidOn) ?? undefined,
      };
      await api.recordPayment(tenancyId, payload);
      notify('Payment recorded');
      onRecorded();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the payment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      icon="rupee"
      title="Record payment"
      subtitle={tenantName}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <GhostBtn onClick={onClose} disabled={busy}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} disabled={!canSubmit} busy={busy}>Record payment</PrimaryBtn>
        </>
      }
    >
      <ErrorBanner message={error} />
      <Field label="Amount received" required>
        <MoneyInput value={amount} onChange={setAmount} placeholder="90000" autoFocus />
      </Field>
      <FieldRow>
        <Field label="Method">
          <SelectInput value={method} onChange={(v) => setMethod(v as typeof method)} options={METHOD_OPTS} />
        </Field>
        <Field label="For month" required hint="Which month's ledger this settles.">
          <TextInput value={period} onChange={setPeriod} type="month" />
        </Field>
      </FieldRow>
      <FieldRow>
        <Field label="Reference" hint="UTR / cheque no. (optional).">
          <TextInput value={reference} onChange={setReference} placeholder="e.g. UTR / cheque no." />
        </Field>
        <Field label="Paid on">
          <TextInput value={paidOn} onChange={setPaidOn} type="date" />
        </Field>
      </FieldRow>
    </Sheet>
  );
}
